import React, { useState, useEffect, useRef, use } from "react";
import axiosInstance from "../api/api.js";
import { useAuth } from "./AuthContext.js";
import Modal from "./showModal.js";
import { DateTime } from "luxon";

const EmployeeTimeTracker = () => {
  const { id: user_id, role } = useAuth();

  const [clockInTime, setClockInTime] = useState(null);
  const [clockOutTime, setClockOutTime] = useState(null);
  const [onBreak, setOnBreak] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [breakElapsedTime, setBreakElapsedTime] = useState(0);
  const [showEarlyClockOutModal, setShowEarlyClockOutModal] = useState(false);
  const [isEarlyClockOutConfirmed, setIsEarlyClockOutConfirmed] = useState(false);
  const [showDateCrossedModal, setShowDateCrossedModal] = useState(false);
  const [totalBreakTime, setTotalBreakTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUsedBreak, setHasUsedBreak] = useState(false);
  const [breakCompleted, setBreakCompleted] = useState(false);
  const [adminSettings, setAdminSettings] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [todaySession, setTodaySession] = useState(null);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [isOnLeaveToday, setIsOnLeaveToday] = useState(false);

  const timerRef = useRef(null);
  const realTimeTimerRef = useRef(null);
  const autoClockOutRef = useRef(null);

  // Load admin settings, holidays and today's session on component mount
  useEffect(() => {
    const initializeComponent = async () => {
      await loadAdminSettings();
      await loadHolidays();
      await loadApprovedLeaves();
      await loadTodaySession();
    };
    initializeComponent();
  }, [user_id]);



  useEffect(() => {
    if (approvedLeaves.length > 0) {
      checkIfOnLeaveToday();
    }
  }, [approvedLeaves, user_id]);

  // Update elapsed time when component state changes
  useEffect(() => {
    if (clockInTime && !clockOutTime) {
      updateElapsedTime();
      startRealTimeTimer();
      scheduleAutoClockOut();
    } else {
      stopRealTimeTimer();
      if (autoClockOutRef.current) {
        clearTimeout(autoClockOutRef.current);
        autoClockOutRef.current = null;
      }
    }
  }, [
    clockInTime,
    clockOutTime,
    totalBreakTime,
    onBreak,
    breakStartTime,
    breakEndTime,
    breakCompleted,
  ]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (realTimeTimerRef.current) {
        clearInterval(realTimeTimerRef.current);
      }
      if (autoClockOutRef.current) {
        clearTimeout(autoClockOutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (onBreak && breakStartTime) {
      // Start a timer to update break elapsed time every second
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          const now = new Date();
          const elapsed = now.getTime() - breakStartTime.getTime();
          setBreakElapsedTime(elapsed);
        }, 1000);
      }
    }
    else {
      // Clear the timer if not on break
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [onBreak, breakStartTime]);

  // Prevent this component from running for non-employee users
  if (role !== 'employee') {
    console.warn('[EmployeeTimeTracker] Component loaded for non-employee user, skipping render');
    return null;
  }

  // console.log("EmployeeTimeTracker user_id:", user_id);

  // API Endpoints
  const API_ENDPOINTS = {
    clockIn: "/clock/clock-in",
    clockOut: "/clock/clock-out",
    startBreak: "/clock/start-break",
    endBreak: "/clock/end-break",
    getTodaySession: `/clock/today/${user_id}`,
    updateSession: "/clock/session",
    adminSettings: "/admin-settings",
    holidays: "/holidays",
    approvedLeaves: "/leave/approved",
    lateClockInCheck: "/clock/late-check",
    autoClockOut: "/clock/auto-clock-out",
  };

  // Helper functions for London timezone
  const getLondonTime = (date = new Date()) => {
    return new Date(
      date.toLocaleString("en-US", { timeZone: "Europe/London" })
    );
  };

  const formatTimeWithAMPM = (date) => {
    if (!date) return "";
    const londonTime = getLondonTime(date);
    return londonTime.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };



  const formatElapsed = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const hrs = String(Math.floor(totalSec / 3600)).padStart(2, "0");
    const mins = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
    const secs = String(totalSec % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  // Convert time string (HH:MM) to milliseconds
  const timeStringToMs = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return (hours * 60 + minutes) * 60 * 1000;
  };

  // Load holidays data
  const loadHolidays = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.holidays);
      if (response.data) {
        setHolidays(response.data);
        // console.log("Holidays loaded:", response.data);
      }
    } catch (error) {
      console.error("Failed to load holidays:", error);
      setHolidays([]);
    }
  };

  const loadApprovedLeaves = async () => {
    try {
      const altResponse = await axiosInstance.get(API_ENDPOINTS.approvedLeaves);
      if (altResponse.data) {
        const userLeaves = altResponse.data.filter(
          (leave) =>
            leave.user_id === user_id || leave.user_id === parseInt(user_id)
        );
        setApprovedLeaves(userLeaves);
        // console.log("Approved leaves loaded (alternative):", userLeaves);
      }
    } catch (altError) {
      console.error("Alternative endpoint also failed:", altError);
      setApprovedLeaves([]);
    }
  };

  const checkIfOnLeaveToday = () => {
    const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    // console.log("Checking leave for date:", today);
    // console.log("User ID:", user_id);
    // console.log("Approved leaves:", approvedLeaves);

    // Filter leaves for current user (handle both string and number user_id)
    const userLeaves = approvedLeaves.filter(
      (leave) =>
        leave.user_id === user_id ||
        leave.user_id === parseInt(user_id) ||
        leave.user_id === String(user_id)
    );

    // console.log("User leaves:", userLeaves);

    const isOnLeave = userLeaves.some((leave) => {
      const startDate = leave.start_date;
      const endDate = leave.end_date;
      const isInRange = today >= startDate && today <= endDate;
      // console.log(
      //   `Leave ${leave.id}: ${startDate} to ${endDate}, isInRange: ${isInRange}`
      // );
      return isInRange;
    });

    // console.log("Is on leave today:", isOnLeave);
    setIsOnLeaveToday(isOnLeave);
    return isOnLeave;
  };

  const getTodayLeaveDetails = () => {
    const today = new Date().toISOString().split("T")[0];

    // Filter leaves for current user (handle both string and number user_id)
    const userLeaves = approvedLeaves.filter(
      (leave) =>
        leave.user_id === user_id ||
        leave.user_id === parseInt(user_id) ||
        leave.user_id === String(user_id)
    );

    const todayLeave = userLeaves.find((leave) => {
      const startDate = leave.start_date;
      const endDate = leave.end_date;
      return today >= startDate && today <= endDate;
    });

    return todayLeave;
  };

  // Check if today is a holiday
  const isTodayHoliday = () => {
    const today = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
    return holidays.some((holiday) => holiday.date === today);
  };

  // Check if today is a weekend
  const isTodayWeekend = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Check if today is overtime (holiday or weekend)
  const isTodayOvertime = () => {
    return isTodayHoliday() || isTodayWeekend();
  };

  // Get today's holiday description if it's a holiday
  const getTodayHolidayDescription = () => {
    const today = new Date().toISOString().split("T")[0];
    const holiday = holidays.find((holiday) => holiday.date === today);
    return holiday ? holiday.description : null;
  };

  // Load admin settings
  const loadAdminSettings = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.adminSettings);
      if (response.data) {
        setAdminSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to load admin settings:", error);
      // Set default values if admin settings fail to load
      setAdminSettings({
        late_clock_in_time: "09:15",
        break_in_time: "13:00",
        break_out_time: "14:00",
        late_clock_out_time: "19:00",
        full_day_hours: "08:30",
        half_day_hours: "05:00",
        short_day_hours: "00:01",
      });
    }
  };

  const onClockOutClick = async () => {
    // Check if the clock-in date is different from current date (date has crossed)
    if (clockInTime) {
      const clockInDate = DateTime.fromJSDate(clockInTime).setZone("Europe/London").toISODate();
      const currentDate = DateTime.now().setZone("Europe/London").toISODate();

      if (clockInDate !== currentDate) {
        setShowDateCrossedModal(true);
        return;
      }
    }

    // Also check if current London time is past 11:59 PM (for same-day late clock-outs)
    const londonTime = DateTime.now().setZone("Europe/London");
    const isPastMidnight = londonTime.hour === 23 && londonTime.minute >= 59;

    if (isPastMidnight) {
      setShowDateCrossedModal(true);
      return;
    }

    const response = await axiosInstance.get(API_ENDPOINTS.adminSettings);
    const fullDayStr = response?.data?.full_day_hours;

    const requiredWorkTime = fullDayStr
      ? (() => {
        const [hours, minutes] = fullDayStr.split(':').map(Number);
        return ((hours * 60) + minutes) * 60 * 1000;
      })()
      : (8.5 * 60 * 60 * 1000);

      console.log("Required working hours", requiredWorkTime)

    if (elapsedTime < requiredWorkTime) {
      setShowEarlyClockOutModal(true); // show modal
    } else {
      handleClockOut(); // no need to confirm
    }
  };


  // Auto clock out at 11:59 PM London time (only for employees)
  const scheduleAutoClockOut = () => {
    // Only schedule auto clock-out for employees
    if (role !== 'employee') {
      console.log('[EmployeeTimeTracker] Skipping auto clock-out scheduling for non-employee user');
      return;
    }

    if (!clockInTime || clockOutTime) return;

    const now = getLondonTime();
    const autoClockOutTime = new Date(now);
    autoClockOutTime.setHours(23, 59, 0, 0); // 11:59 PM

    // If it's already past 11:59 PM, schedule for next day
    if (now > autoClockOutTime) {
      autoClockOutTime.setDate(autoClockOutTime.getDate() + 1);
    }

    const timeUntilAutoClockOut = autoClockOutTime.getTime() - now.getTime();

    if (autoClockOutRef.current) {
      clearTimeout(autoClockOutRef.current);
    }

    autoClockOutRef.current = setTimeout(() => {
      console.log("[EmployeeTimeTracker] Auto clock out triggered at 11:59 PM London time");
      handleAutoClockOut();
    }, timeUntilAutoClockOut);

  };

  // Handle auto clock out
  const handleAutoClockOut = async () => {
    try {
      // End break if currently on break
      if (onBreak) {
        await handleBreakEnd();
      }

      // Set clock out time to 11:59 PM London time
      const autoClockOutTime = getLondonTime();
      autoClockOutTime.setHours(23, 59, 0, 0);

      const response = await axiosInstance.post(API_ENDPOINTS.clockOut, {
        user_id,
        sessionId: todaySession?.id || todaySession?._id,
        clockOutTime: null,
        totalWorkedTime: elapsedTime,
        totalBreakTime,
        autoClockOut: true,
      });

      if (response.data.success) {
        const session = response.data.attendance;
        setClockOutTime(null);
        stopRealTimeTimer();
        setTodaySession(session);
        refreshAttendanceData();
        // console.log("Auto clock out completed successfully");
      }
    } catch (error) {
      console.error("Auto clock out failed:", error);
      setError("Auto clock out failed. Please manually clock out.");
    }
  };



  // Real-time timer that updates every second
  const startRealTimeTimer = () => {
    if (!realTimeTimerRef.current && clockInTime && !clockOutTime) {
      realTimeTimerRef.current = setInterval(() => {
        updateElapsedTime();
      }, 1000);
    }
  };

  const stopRealTimeTimer = () => {
    if (realTimeTimerRef.current) {
      clearInterval(realTimeTimerRef.current);
      realTimeTimerRef.current = null;
    }
  };

  const updateElapsedTime = () => {
    if (!clockInTime || clockOutTime) return;

    const now = new Date();
    let elapsed = now.getTime() - clockInTime.getTime();

    // If break is completed, subtract the total break duration
    if (breakCompleted && breakStartTime && breakEndTime) {
      const breakDuration = breakEndTime.getTime() - breakStartTime.getTime();
      elapsed -= breakDuration;
    }

    // If currently on break, don't count the current break time
    if (onBreak && breakStartTime) {
      const currentBreakDuration = now.getTime() - breakStartTime.getTime();
      elapsed -= currentBreakDuration;
    }

    setElapsedTime(Math.max(0, elapsed));
  };

  // Calculate remaining break time dynamically using admin settings
  const calculateRemainingBreakTime = () => {
    if (!adminSettings) {
      // Fallback to 1 hour if admin settings not loaded
      const totalBreakAllowed = 60 * 60 * 1000; // 1 hour in ms

      if (onBreak && breakStartTime) {
        const now = new Date();
        const currentBreakDuration = now.getTime() - breakStartTime.getTime();
        return totalBreakAllowed - currentBreakDuration;
      } else if (breakCompleted && breakStartTime && breakEndTime) {
        const usedBreakTime = breakEndTime.getTime() - breakStartTime.getTime();
        return totalBreakAllowed - usedBreakTime;
      }

      return totalBreakAllowed;
    }

    // Calculate break duration from admin settings using total_break_time
    const totalBreakHours = parseFloat(adminSettings.total_break_time) || 1; // Default to 1 hour if not found
    const totalBreakAllowed = totalBreakHours * 60 * 60 * 1000; // Convert hours to milliseconds

    if (onBreak && breakStartTime) {
      const now = new Date();
      const currentBreakDuration = now.getTime() - breakStartTime.getTime();
      return totalBreakAllowed - currentBreakDuration;
    } else if (breakCompleted && breakStartTime && breakEndTime) {
      const usedBreakTime = breakEndTime.getTime() - breakStartTime.getTime();
      return totalBreakAllowed - usedBreakTime;
    }

    return totalBreakAllowed;
  };
  // Callback function to refresh attendance data
  const refreshAttendanceData = () => {
    window.dispatchEvent(new CustomEvent("refreshAttendance"));
  };

  const loadTodaySession = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(API_ENDPOINTS.getTodaySession);
      // console.log("BACKEND RESPONSE:", response.data);
      const sessionData = response.data;

      if (sessionData && sessionData.success) {
        const session = sessionData.data;
        setTodaySession(session);

        // Set clock times
        if (session.clock_in) {
          setClockInTime(new Date(session.clock_in));
        }
        if (session.clock_out) {
          setClockOutTime(new Date(session.clock_out));
        }

        // Handle break logic based on DB structure
        const hasBreakStart = session.break_start;
        const hasBreakEnd = session.break_end;

        if (hasBreakStart && hasBreakEnd) {
          // Break completed
          setBreakStartTime(new Date(session.break_start));
          setBreakEndTime(new Date(session.break_end));
          setBreakCompleted(true);
          setOnBreak(false);
          setHasUsedBreak(true);

          // Calculate total break time used
          const breakDuration =
            new Date(session.break_end).getTime() -
            new Date(session.break_start).getTime();
          setTotalBreakTime(breakDuration);
        } else if (hasBreakStart && !hasBreakEnd) {
          // Currently on break
          setBreakStartTime(new Date(session.break_start));
          setBreakEndTime(null);
          setBreakCompleted(false);
          setOnBreak(true);
          setHasUsedBreak(true);
          setTotalBreakTime(0);
        } else {
          // No break used
          setBreakStartTime(null);
          setBreakEndTime(null);
          setBreakCompleted(false);
          setOnBreak(false);
          setHasUsedBreak(false);
          setTotalBreakTime(0);
        }

        // Handle elapsed time calculation based on session state
        if (session.clock_in) {
          if (session.clock_out === null) {
            // Absent employee (clock_out = null) - calculate worked time and stop all timers
            const now = new Date();
            let totalWorked = now.getTime() - new Date(session.clock_in).getTime();

            // Subtract break time if break is completed
            if (hasBreakStart && hasBreakEnd) {
              const breakDuration =
                new Date(session.break_end).getTime() -
                new Date(session.break_start).getTime();
              totalWorked -= breakDuration;
            }

            setElapsedTime(Math.max(0, totalWorked));
            // Stop any running timers for absent sessions
            stopRealTimeTimer();
            if (autoClockOutRef.current) {
              clearTimeout(autoClockOutRef.current);
              autoClockOutRef.current = null;
            }

          } else if (session.clock_out) {
            // Properly clocked out session - calculate total worked time
            let totalWorked =
              new Date(session.clock_out).getTime() - new Date(session.clock_in).getTime();
            if (hasBreakStart && hasBreakEnd) {
              const breakDuration =
                new Date(session.break_end).getTime() -
                new Date(session.break_start).getTime();
              totalWorked -= breakDuration;
            }
            setElapsedTime(Math.max(0, totalWorked));
          } else {
            // Truly active session (no clock_out at all) - start real-time timer
            const now = new Date();
            let calculatedElapsed =
              now.getTime() - new Date(session.clock_in).getTime();

            // Subtract break time if break is completed
            if (hasBreakStart && hasBreakEnd) {
              const breakDuration =
                new Date(session.break_end).getTime() -
                new Date(session.break_start).getTime();
              calculatedElapsed -= breakDuration;
            }

            // If currently on break, subtract current break duration
            if (hasBreakStart && !hasBreakEnd) {
              const currentBreakDuration =
                now.getTime() - new Date(session.break_start).getTime();
              calculatedElapsed -= currentBreakDuration;
            }

            setElapsedTime(Math.max(0, calculatedElapsed));
            startRealTimeTimer();
            scheduleAutoClockOut(); // Only schedule for truly active sessions
          }
        }
      } else {
        // No session found for today - reset all states
        resetDailyStates();
      }
    } catch (error) {
      console.error("Load session error:", error);
      if (error.response?.status === 404) {
        // No session found for today
        resetDailyStates();
        setError(null);
      } else {
        setError("Failed to load today's session");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetDailyStates = () => {
    setClockInTime(null);
    setClockOutTime(null);
    setOnBreak(false);
    setBreakStartTime(null);
    setBreakEndTime(null);
    setElapsedTime(0);
    setTotalBreakTime(0);
    setHasUsedBreak(false);
    setBreakCompleted(false);
    setTodaySession(null);

    // Clear auto clock out timer
    if (autoClockOutRef.current) {
      clearTimeout(autoClockOutRef.current);
      autoClockOutRef.current = null;
    }
  };

  const checkLateClockIn = async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.lateClockInCheck);
      console.log('Late check response:', res);
      if (res.data.late) {
        console.warn('You clocked in late today!');
        // Optional: Trigger toast or show in UI
      }
    } catch (err) {
      console.error('Late check failed:', err);
    }
  };

  const handleClockIn = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(API_ENDPOINTS.clockIn, {
        user_id,
        clockInTime: new Date().toISOString(),
      });

      if (response.data.success) {
        const session = response.data.attendance;
        const clockInDateTime = new Date(session.clock_in);

        setClockInTime(clockInDateTime);
        setClockOutTime(null);
        setElapsedTime(0);
        setTotalBreakTime(0);
        setOnBreak(false);
        setHasUsedBreak(false);
        setBreakCompleted(false);
        setBreakStartTime(null);
        setBreakEndTime(null);
        setTodaySession(session);

        startRealTimeTimer();
        scheduleAutoClockOut(); // Schedule auto clock out
        refreshAttendanceData();
        checkLateClockIn(); // Check if clocked in late
      } else {
        throw new Error(response.data.message || "Failed to clock in");
      }
    } catch (error) {
      console.error("Clock in error:", error);
      setError(
        error.response?.data?.message || "Failed to clock in. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setLoading(true);
      setError(null);

      // End break if currently on break
      if (onBreak) {
        await handleBreakEnd();
      }

      const response = await axiosInstance.post(API_ENDPOINTS.clockOut, {
        user_id,
        sessionId: todaySession?.id || todaySession?._id,
        clockOutTime: new Date().toISOString(),
        totalWorkedTime: elapsedTime,
        totalBreakTime,
      });

      // console.log("CLOCK OUT RESPONSE:", response.data);

      if (response.data.success) {
        const session = response.data.attendance;
        setClockOutTime(new Date(session.clock_out));
        stopRealTimeTimer();
        setTodaySession(session);

        // Clear auto clock out timer
        if (autoClockOutRef.current) {
          clearTimeout(autoClockOutRef.current);
          autoClockOutRef.current = null;
        }

        refreshAttendanceData();
      } else {
        throw new Error(response.data.message || "Failed to clock out");
      }
    } catch (error) {
      console.error("Clock out error:", error.response?.data || error.message);
      setError(
        error.response?.data?.error || "Failed to clock out. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBreakStart = async () => {
    try {
      setLoading(true);
      setError(null);
      stopRealTimeTimer();

      const response = await axiosInstance.post(API_ENDPOINTS.startBreak, {
        user_id,
        sessionId: todaySession?.id || todaySession?._id,
        breakStartTime: new Date().toISOString(),
      });

      if (response.data.success) {
        const newBreakStartTime = new Date();
        setBreakStartTime(newBreakStartTime);
        setBreakEndTime(null);
        setOnBreak(true);
        setHasUsedBreak(true);
        setBreakCompleted(false);
        setTodaySession(response.data.data || response.data.attendance || response.data);
        refreshAttendanceData();
      } else {
        throw new Error(response.data.message || response.data.error || "Failed to start break");
      }
    } catch (error) {
      console.error("Start break error details:", {
        error,
        response: error.response?.data,
        status: error.response?.status,
        message: error.message,
      });

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to start break. Please try again.";
      setError(`Break Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };


  const handleBreakEnd = async () => {
    try {
      setLoading(true);
      setError(null);
      startRealTimeTimer();

      if (!breakStartTime) {
        throw new Error("No break start time found");
      }

      const breakEndDateTime = new Date();
      const breakDuration =
        breakEndDateTime.getTime() - breakStartTime.getTime();
      const BREAK_EXCEED_THRESHOLD = 60 * 60 * 1000; // 60 minutes in ms

      const response = await axiosInstance.post(API_ENDPOINTS.endBreak, {
        user_id,
        sessionId: todaySession?.id || todaySession?._id,
        breakEndTime: breakEndDateTime.toISOString(),
        breakDuration,
      });

      // console.log("End break response:", response.data);

      if (response.data.success) {
        setBreakEndTime(breakEndDateTime);
        setTotalBreakTime(breakDuration);
        setOnBreak(false);
        setBreakCompleted(true);
        setTodaySession(
          response.data.data || response.data.attendance || response.data
        );
        refreshAttendanceData();
        if (breakDuration > BREAK_EXCEED_THRESHOLD) {
          try {
            await axiosInstance.post('/notifications/break-exceeded', {
              user_id,
              breakDuration,
            });
          } catch (notifyError) {
            console.warn('Break exceeded notification failed:', notifyError.message);
          }
        }
      } else {
        throw new Error(
          response.data.message || response.data.error || "Failed to end break"
        );
      }
    } catch (error) {
      console.error("End break error details:", {
        error,
        response: error.response?.data,
        status: error.response?.status,
        message: error.message,
      });

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to end break. Please try again.";
      setError(`Break Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Calculate remaining break time dynamically
  const remainingBreakTime = calculateRemainingBreakTime();

  // Calculate overtime using the original logic (8.5 hours standard) but with enhanced overtime detection
  const calculateOvertimeMs = () => {
    const standardWorkDayStr = adminSettings?.full_day_hours || "08:30";

    // Convert "08:30" => milliseconds
    const [hours, minutes] = standardWorkDayStr.split(":").map(Number);
    const standardWorkDay = (hours * 60 * 60 + minutes * 60) * 1000;

    if (isTodayOvertime()) {
      return elapsedTime;
    }

    return Math.max(0, elapsedTime - standardWorkDay);
  };

  const overtimeMs = Math.max(0, calculateOvertimeMs()); // Prevent negative overtime

  if (loading && !clockInTime && !error) {
    return (
      <div className="card time-tracking">
        <div className="loading-state">Loading your session...</div>
      </div>
    );
  }

  return (
    <div className="card time-tracking">
      <h2 className="card-title">Today Time Utilisation</h2>

      {error && (
        <div
          className="error-message"
          style={{
            backgroundColor: "#fee2e2",
            color: "#dc2626",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "16px",
            border: "1px solid #fecaca",
          }}
        >
          {error}
        </div>
      )}

      {/* Buttons Row */}
      <div className="time-tracking-buttons">
        <div className="button-group-left">
          {!clockInTime ? (
            isOnLeaveToday ? (
              <div
                className="leave-status"
                style={{
                  backgroundColor: "#f3e8ff",
                  color: "#7c3aed",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #c4b5fd",
                  textAlign: "center",
                }}
              >
                You are on {getTodayLeaveDetails()?.type} leave today
                <br />
                <small>Reason: {getTodayLeaveDetails()?.reason}</small>
              </div>
            ) : (
              <button
                onClick={handleClockIn}
                disabled={loading}
                className="btn btn-blue clock-in-btn"
              >
                {loading ? "Clocking In..." : "Clock In"}
              </button>
            )
          ) : (
            <div className="clock-status">
              Clocked in at: {formatTimeWithAMPM(clockInTime)}
            </div>
          )}
        </div>

        <div className="button-group-right">
          {clockInTime && !clockOutTime ? (
            <button
              onClick={onClockOutClick}
              disabled={loading}
              className="btn btn-blue clock-out-btn"
            >
              {loading ? "Clocking Out..." : "Clock Out"}
            </button>
          ) : (
            clockOutTime && (
              <div className="clock-status">
                Clocked out at: {formatTimeWithAMPM(clockOutTime)}
              </div>
            )
          )}
        </div>
      </div>

      {/* Timer Display */}
      <div className="timer-display">
        <h3>{formatElapsed(elapsedTime)}</h3>
        {onBreak && (
          <div style={{ fontSize: "14px", color: "#f59e0b", marginTop: "8px" }}>
            On Break
          </div>
        )}
      </div>

      {/* Break Controls Row - Show only if not completed and clocked in */}
      {clockInTime && !clockOutTime && !breakCompleted && (
        <div className="time-tracking-buttons">
          <div className="button-group-left">
            <button
              onClick={onBreak ? handleBreakEnd : handleBreakStart}
              disabled={loading}
              className={`btn ${onBreak ? "btn-red" : "btn-yellow"}`}
            >
              {loading
                ? onBreak
                  ? "Ending Break..."
                  : "Starting Break..."
                : onBreak
                ? "End Break"
                : "Start Break"}
            </button>
          </div>

          <div className="button-group-right">
            {onBreak && breakStartTime && (
              <div className="clock-status">
                Break started at: {formatTimeWithAMPM(breakStartTime)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Show break completion status */}
      {breakCompleted && (
        <div className="time-tracking-buttons">
          <div className="button-group-left">
            <div className="clock-status">Daily break completed</div>
          </div>
          <div className="button-group-right">
            {breakStartTime && breakEndTime && (
              <div className="clock-status">
                Break: {formatTimeWithAMPM(breakStartTime)} -{" "}
                {formatTimeWithAMPM(breakEndTime)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Time Breakdown - Original Interface Style */}
      <div className="time-breakdown">
        <div className="time-item">
          <div className="color-indicator purple"></div>
          <span>
            Break left: {remainingBreakTime < 0 ? "-" : ""}
            {formatElapsed(Math.abs(remainingBreakTime))}
          </span>
        </div>
        <div className="time-item">
          <div className="color-indicator green"></div>
          <span>Extratime: {formatElapsed(overtimeMs)}</span>
        </div>
      </div>

      <Modal
        isOpen={showEarlyClockOutModal}
        onClose={() => setShowEarlyClockOutModal(false)}
        title="Confirm Early Clock Out"
        message="You are about to clock out before completing 8 hours 30 minutes. Are you sure?"
        confirmLabel="Yes, Clock Out"
        cancelLabel="Cancel"
        type="warning"
        onConfirm={handleClockOut}
      />


      {/* Date Crossed Modal */}
      <Modal
        isOpen={showDateCrossedModal}
        onClose={async () => {
          console.log("Date crossed modal closed - starting auto clock-out");
          try {
            if (onBreak) {
              await handleBreakEnd();
            }
            // Use the autoClockOut endpoint for date-crossed scenarios
            const response = await axiosInstance.post(API_ENDPOINTS.autoClockOut, {
              user_id,
            });
            if (response.data.success) {
              console.log("Auto clock out successful, resetting state for new day");
              // Clear all state and stop any running processes
              resetDailyStates();
              refreshAttendanceData();

              // Force a new session check to ensure clean state
              await new Promise(resolve => setTimeout(resolve, 100));
              await loadTodaySession();
            }
          } catch (error) {
            console.error("Auto clock out on date crossed failed:", error);
            setError("Auto clock out failed. Please contact admin.");
          } finally {
            // Always close the modal
            setShowDateCrossedModal(false);
          }
        }}
        title="Date Has Crossed"
        message="The date has crossed. Your session will be ended automatically and you can clock in again tomorrow."
        confirmLabel="OK"
        type="error"
      />

      {/* Add a second modal if you want a 'Yes' and 'Cancel' button.
    Or modify the existing modal to support two buttons. */}
      <Modal
        isOpen={isEarlyClockOutConfirmed}
        onClose={() => setIsEarlyClockOutConfirmed(false)}
        title="Early Clock Out Confirmed"
        message="You have clocked out early. Please ensure you have completed all tasks."
        confirmLabel="OK"
        type="info"
      />

    </div>
  );
};

export default EmployeeTimeTracker;
